import { processText } from '../../../../utils/learnUtils';
import { useMathJax } from '../../../../hooks/useMathJax';
import { lazy, Suspense, useRef } from 'react';
import IntFigSlider from './IntFigSlider';
import IntFigCheckbox from './IntFigCheckbox';
import IntFigPoint from './IntFigPoint';
import IntFigMovablePoint from './IntFigMovablePoint';
import IntFigAccordion from './IntFigAccordion';
import IntFigLine from './IntFigLine';
import IntFigLine2D from './IntFigLine2D';
import IntFigFunction from './IntFigFunction';
import IntFigCFunction from './IntFigCFunction';
import IntFigSlice from './IntFigSlice';

const IntFigContourPlot = lazy(() => import('./IntFigContourPlot'));
const IntFigBinaryMap   = lazy(() => import('./IntFigBinaryMap'));

/* Coerce a possibly-malformed value (string, undefined, NaN, non-number
   mathjs result, ...) to a fixed-point string, falling back to '0' instead
   of throwing when the value can't be treated as a number. */
function toFixedSafe(v, digits) {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(digits) : '0';
}

/* Substitute parameter values into an expression string.
   Replaces tp1, tp0, ... then p11, p10, ... then d9, d8, ... (right-to-left
   to avoid e.g. "p1" matching inside "p10"). */
export function preprocessExpr(expr, params, tParams, derived = []) {
  let s = expr;
  for (let i = tParams.length - 1; i >= 0; i--) {
    s = s.replaceAll(`tp${i}`, toFixedSafe(tParams[i], 6));
  }
  for (let i = params.length - 1; i >= 0; i--) {
    s = s.replace(new RegExp(`p${i}(\\D|$)`, 'g'), `${toFixedSafe(params[i], 6)}$1`);
  }
  for (let i = derived.length - 1; i >= 0; i--) {
    s = s.replaceAll(`d${i}`, toFixedSafe(derived[i], 6));
  }
  s = s.replaceAll(/\s/g, '');
  s = s.replaceAll('+-', '-');
  return s;
}

/* Return the z-index for an overlay component */
function zIndex(component) {
  return (component.z_index ?? 0) + 10;
}

/* Wrapper div for all overlay types */
function Overlay({ component, children, className = '', passThrough = false }) {
  const style = {
    left: component.left,
    top: component.top,
    width: component.width,
    height: component.height,
    zIndex: zIndex(component),
    ...(passThrough && { pointerEvents: 'none' }),
  };
  return (
    <div className={`ct__intfig-ov${className ? ' ' + className : ''}`} style={style}>
      {children}
    </div>
  );
}

/* Text overlay — inline MathJax rendering */
function TextOverlay({ component, derived, zoom }) {
  const textRef = useRef(null);
  const precision = component.is_integer ? 0 : 2;
  const value = toFixedSafe(
    derived.length > component.derived ? derived[component.derived] : 0,
    precision,
  );
  const textHtml = processText(component.text);
  useMathJax(textRef, [textHtml]);
  const fontSize = zoom ? '2vw' : '1em';
  return (
    <div className="ct__intfig-text" style={{ fontSize }}>
      <span ref={textRef} dangerouslySetInnerHTML={{ __html: textHtml }} />
      <span>{value}</span>
    </div>
  );
}

/**
 * Dispatches each interactive component overlay by type.
 * @param {{ item, params, tParams, tParamsCum, derived, setParam, zoom }} props
 */
export default function IntFigComponents({ item, params, tParams, tParamsCum, derived, setParam, zoom = false }) {
  return (
    <>
      {item.data.components.map((c) => {
        switch (c.type) {
          case 'EDCOMP_INT_SLIDER':
            return (
              <Overlay key={c._id} component={c}>
                <IntFigSlider
                  value={params[c.param]}
                  min={c.min}
                  max={c.max}
                  name={c.name}
                  color={c.color}
                  onChange={(v) => setParam(c.param, v)}
                />
              </Overlay>
            );

          case 'EDCOMP_INT_CHECKBOX':
            return (
              <Overlay key={c._id} component={c}>
                <IntFigCheckbox
                  checked={params[c.param] === 1}
                  onChange={(v) => setParam(c.param, v ? 1 : 0)}
                />
              </Overlay>
            );

          case 'EDCOMP_INT_POINT':
            return (
              <Overlay key={c._id} component={c}>
                <IntFigPoint
                  axis={{ min_x: c.min_x, max_x: c.max_x, min_y: c.min_y, max_y: c.max_y }}
                  x={derived.length > c.p1x ? derived[c.p1x] : 0.5}
                  y={derived.length > c.p1y ? derived[c.p1y] : 0.2}
                  color={c.color}
                  color1={c.color1}
                  strokeWidth={c.strokeWidth}
                  size={c.size}
                  yIsUp={c.y_is_up}
                />
              </Overlay>
            );

          case 'EDCOMP_INT_MOVABLE_POINT':
            return (
              <Overlay key={c._id} component={c}>
                <IntFigMovablePoint
                  x={params[c.param_x]}
                  y={params[c.param_y]}
                  axis={{ min_x: c.min_x, max_x: c.max_x, min_y: c.min_y, max_y: c.max_y }}
                  color={c.color}
                  size={c.size}
                  yIsDown={!c.y_is_up}
                  showRegion={c.showRegion ?? false}
                  onChange={(x, y) => setParam([c.param_x, c.param_y], [x, y])}
                />
              </Overlay>
            );

          case 'EDCOMP_INT_ACCORDION':
            return (
              <Overlay key={c._id} component={c} passThrough>
                <IntFigAccordion
                  xs={[params[c.param_x], params[c.param_x2], params[c.param_x3], params[c.param_x4]]}
                  ys={[params[c.param_y], params[c.param_y2], params[c.param_y3], params[c.param_y4]]}
                  axis={{ min_x: c.min_x, max_x: c.max_x, min_y: c.min_y, max_y: c.max_y }}
                  color={c.color}
                  size={c.size}
                  onChange={(xs, ys) => setParam(
                    [c.param_x, c.param_x2, c.param_x3, c.param_x4,
                      c.param_y, c.param_y2, c.param_y3, c.param_y4],
                    [...xs, ...ys]
                  )}
                />
              </Overlay>
            );

          case 'EDCOMP_INT_LINE':
            return (
              <Overlay key={c._id} component={c}>
                <IntFigLine
                  axis={{ min_x: c.min_x, max_x: c.max_x, min_y: c.min_y, max_y: c.max_y }}
                  x1={derived.length > c.p1x ? derived[c.p1x] : 0}
                  x2={derived.length > c.p2x ? derived[c.p2x] : 0}
                  y1={derived.length > c.p1y ? derived[c.p1y] : 0.2}
                  y2={derived.length > c.p2y ? derived[c.p2y] : 0.3}
                  color={c.color}
                  dashArray={`${c.strokeDashArray?.[0] ?? 0} ${c.strokeDashArray?.[1] ?? 0}`}
                  strokeWidth={c.strokeWidth}
                  showRegion={c.showRegion ?? false}
                  arrowStyle={c.arrowStyle ?? 1}
                  arrowWidth={c.arrowWidth ?? 1}
                />
              </Overlay>
            );

          case 'EDCOMP_INT_LINE2D':
            return (
              <Overlay key={c._id} component={c}>
                <IntFigLine2D
                  axis={{ min_x: c.min_x, max_x: c.max_x, min_y: c.min_y, max_y: c.max_y }}
                  intercept={derived.length > c.intercept ? derived[c.intercept] : 0}
                  slopeX={derived.length > c.slope_x ? derived[c.slope_x] : 0}
                  slopeY={derived.length > c.slope_y ? derived[c.slope_y] : 0.2}
                  color={c.color}
                  dashArray={`${c.strokeDashArray?.[0] ?? 0} ${c.strokeDashArray?.[1] ?? 0}`}
                  strokeWidth={c.strokeWidth}
                  showRegion={c.showRegion ?? false}
                />
              </Overlay>
            );

          case 'EDCOMP_INT_CONTOURPLOT': {
            const expr = preprocessExpr(c.function, params, tParams, derived);
            return (
              <Overlay key={c._id} component={c}>
                <Suspense fallback={null}>
                  <IntFigContourPlot
                    axis={{ min_x: c.min_x, max_x: c.max_x, min_y: c.min_y, max_y: c.max_y, min_z: Number(c.min_z), max_z: Number(c.max_z) }}
                    expr={expr}
                    noPixels={c.no_pixels ?? 80}
                    showRegion={c.showRegion ?? false}
                  />
                </Suspense>
              </Overlay>
            );
          }

          case 'EDCOMP_INT_BINARY_MAP': {
            const expr = preprocessExpr(c.function, params, tParams, derived);
            return (
              <Overlay key={c._id} component={c}>
                <Suspense fallback={null}>
                  <IntFigBinaryMap
                    axis={{ min_x: c.min_x, max_x: c.max_x, min_y: c.min_y, max_y: c.max_y }}
                    expr={expr}
                    color0={c.color0}
                    color1={c.color1}
                    noPixels={c.no_pixels ?? 80}
                    showRegion={c.showRegion ?? false}
                  />
                </Suspense>
              </Overlay>
            );
          }

          case 'EDCOMP_INT_FUNCTION':
            return (
              <Overlay key={c._id} component={c}>
                <IntFigFunction
                  axis={{ min_x: c.min_x, max_x: c.max_x, min_y: c.min_y, max_y: c.max_y }}
                  params={params}
                  tParams={tParams}
                  derived={derived}
                  functionText={c.function}
                  color={c.color}
                  dashArray={`${c.strokeDashArray?.[0] ?? 0} ${c.strokeDashArray?.[1] ?? 0}`}
                  strokeWidth={c.strokeWidth}
                  noSegments={c.no_segments ?? 100}
                  showRegion={c.showRegion ?? false}
                />
              </Overlay>
            );

          case 'EDCOMP_INT_CFUNCTION':
            return (
              <Overlay key={c._id} component={c}>
                <IntFigCFunction
                  axis={{ min_x: c.min_x, max_x: c.max_x, min_y: c.min_y, max_y: c.max_y }}
                  tParamsCum={tParamsCum}
                  paramX={c.param_x}
                  paramY={c.param_y}
                  color={c.color}
                  dashArray={`${c.strokeDashArray?.[0] ?? 0} ${c.strokeDashArray?.[1] ?? 0}`}
                  strokeWidth={c.strokeWidth}
                  showRegion={c.showRegion ?? false}
                />
              </Overlay>
            );

          case 'EDCOMP_INT_SLICE': {
            /* Adjust geometry to avoid SVG shearing (from original) */
            let cW    = parseFloat(c.width);
            let cTop  = parseFloat(c.top);
            let cLeft = parseFloat(c.left);
            let cH    = parseFloat(c.height);
            const whr = item.data.width_height_ratio ?? 1;
            let clipRatio = 1;
            if (c.isVertical && cW < cH * 0.75) {
              clipRatio = (cH / whr) / cW;
              cW = cH / whr;
            } else if (!c.isVertical && cW * whr > cH * 0.75) {
              cTop = cTop + (cH - cW * whr);
              clipRatio = (cW * whr) / cH;
              cH = cW * whr;
            }
            const adjustedC = { ...c, left: `${cLeft}%`, top: `${cTop}%`, width: `${cW}%`, height: `${cH}%` };
            let functionArray;
            try {
              functionArray = JSON.parse(c.function);
            } catch {
              functionArray = [];
            }
            return (
              <Overlay key={c._id} component={adjustedC}>
                <IntFigSlice
                  axis={{ min_x: c.min_x, max_x: c.max_x, min_y: c.min_y, max_y: c.max_y }}
                  functionArray={functionArray}
                  paramY={derived[c.param_y] ?? 0}
                  color={c.color}
                  dashArray={`${c.strokeDashArray?.[0] ?? 0} ${c.strokeDashArray?.[1] ?? 0}`}
                  strokeWidth={c.strokeWidth}
                  isVertical={c.isVertical ?? false}
                  clippingRatio={clipRatio}
                />
              </Overlay>
            );
          }

          case 'EDCOMP_INT_TEXT':
            return (
              <Overlay key={c._id} component={c}>
                <TextOverlay component={c} derived={derived} zoom={zoom} />
              </Overlay>
            );

          default:
            return null;
        }
      })}
    </>
  );
}
